// =============================================================================
// SIREPPAR - Página de Configuración del Sistema
// =============================================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../services/api';

interface ConfigItem {
  clave: string;
  valor: string;
  descripcion: string;
  tipo: 'texto' | 'numero' | 'booleano' | 'json';
  categoria: string;
}

const categorias = [
  { id: 'general', label: 'General', icon: '⚙️' },
  { id: 'partidos', label: 'Partidos', icon: '🏛️' },
  { id: 'expedientes', label: 'Expedientes', icon: '📁' },
  { id: 'seguridad', label: 'Seguridad', icon: '🔐' },
  { id: 'notificaciones', label: 'Notificaciones', icon: '🔔' },
];

// Configuración por defecto del sistema
const defaultConfig: ConfigItem[] = [
  // General
  { clave: 'NOMBRE_SISTEMA', valor: 'SIREPPAR', descripcion: 'Nombre del sistema', tipo: 'texto', categoria: 'general' },
  { clave: 'NOMBRE_PROVINCIA', valor: 'Chubut', descripcion: 'Nombre de la provincia', tipo: 'texto', categoria: 'general' },
  { clave: 'EMAIL_CONTACTO', valor: 'secretaria@electoral.chubut.gob.ar', descripcion: 'Email de contacto', tipo: 'texto', categoria: 'general' },
  { clave: 'TELEFONO_CONTACTO', valor: '(0280) 448-0000', descripcion: 'Teléfono de contacto', tipo: 'texto', categoria: 'general' },
  
  // Partidos
  { clave: 'AFILIADOS_MINIMO_PERMIL', valor: '4', descripcion: 'Mínimo de afiliados (‰ del padrón)', tipo: 'numero', categoria: 'partidos' },
  { clave: 'AUTORIDADES_MINIMO', valor: '5', descripcion: 'Mínimo de autoridades requeridas', tipo: 'numero', categoria: 'partidos' },
  { clave: 'APODERADOS_MINIMO', valor: '2', descripcion: 'Mínimo de apoderados requeridos', tipo: 'numero', categoria: 'partidos' },
  { clave: 'PARIDAD_GENERO_REQUERIDA', valor: 'true', descripcion: 'Exigir paridad de género en listas', tipo: 'booleano', categoria: 'partidos' },
  { clave: 'PORCENTAJE_PARIDAD', valor: '50', descripcion: 'Porcentaje mínimo de cada género', tipo: 'numero', categoria: 'partidos' },
  
  // Expedientes
  { clave: 'PLAZO_RECONOCIMIENTO_DIAS', valor: '90', descripcion: 'Plazo para reconocimiento (días)', tipo: 'numero', categoria: 'expedientes' },
  { clave: 'PLAZO_CAMBIO_AUTORIDADES_DIAS', valor: '30', descripcion: 'Plazo para cambio de autoridades', tipo: 'numero', categoria: 'expedientes' },
  { clave: 'PLAZO_SUBSANACION_DIAS', valor: '15', descripcion: 'Plazo para subsanar observaciones', tipo: 'numero', categoria: 'expedientes' },
  { clave: 'MAX_OBSERVACIONES', valor: '10', descripcion: 'Máximo de observaciones por expediente', tipo: 'numero', categoria: 'expedientes' },
  
  // Seguridad
  { clave: 'INTENTOS_LOGIN_MAX', valor: '5', descripcion: 'Máximo intentos de login fallidos', tipo: 'numero', categoria: 'seguridad' },
  { clave: 'BLOQUEO_MINUTOS', valor: '15', descripcion: 'Minutos de bloqueo tras intentos fallidos', tipo: 'numero', categoria: 'seguridad' },
  { clave: 'SESION_TIMEOUT_HORAS', valor: '8', descripcion: 'Duración de sesión (horas)', tipo: 'numero', categoria: 'seguridad' },
  { clave: 'MFA_OBLIGATORIO_ADMIN', valor: 'true', descripcion: 'MFA obligatorio para administradores', tipo: 'booleano', categoria: 'seguridad' },
  { clave: 'PASSWORD_MIN_LENGTH', valor: '8', descripcion: 'Longitud mínima de contraseña', tipo: 'numero', categoria: 'seguridad' },
  
  // Notificaciones
  { clave: 'NOTIF_EMAIL_HABILITADO', valor: 'true', descripcion: 'Enviar notificaciones por email', tipo: 'booleano', categoria: 'notificaciones' },
  { clave: 'NOTIF_VENCIMIENTO_DIAS', valor: '5', descripcion: 'Días antes de vencimiento para alertar', tipo: 'numero', categoria: 'notificaciones' },
  { clave: 'EMAIL_FROM', valor: 'noreply@sireppar.gob.ar', descripcion: 'Email remitente', tipo: 'texto', categoria: 'notificaciones' },
];

export default function ConfiguracionPage() {
  const queryClient = useQueryClient();
  const [activeCategoria, setActiveCategoria] = useState('general');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  // Query configuración
  const { data: config, isLoading } = useQuery({
    queryKey: ['configuracion'],
    queryFn: async () => {
      try {
        const response = await api.get('/configuracion');
        return response.data.data as ConfigItem[];
      } catch {
        // Si no hay endpoint, usar config por defecto
        return defaultConfig;
      }
    },
  });

  // Mutation guardar
  const saveMutation = useMutation({
    mutationFn: async (data: { clave: string; valor: string }) => {
      return api.put(`/configuracion/${data.clave}`, { valor: data.valor });
    },
    onSuccess: () => {
      toast.success('Configuración actualizada');
      queryClient.invalidateQueries({ queryKey: ['configuracion'] });
      setEditingKey(null);
    },
    onError: () => {
      toast.error('Error al guardar configuración');
    },
  });

  const startEdit = (item: ConfigItem) => {
    setEditingKey(item.clave);
    setEditValue(item.valor);
  };

  const saveEdit = (clave: string) => {
    saveMutation.mutate({ clave, valor: editValue });
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const renderValue = (item: ConfigItem, isEditing: boolean) => {
    if (isEditing) {
      if (item.tipo === 'booleano') {
        return (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="border rounded px-2 py-1"
          >
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>
        );
      }
      return (
        <input
          type={item.tipo === 'numero' ? 'number' : 'text'}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="border rounded px-2 py-1 w-48"
        />
      );
    }

    if (item.tipo === 'booleano') {
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          item.valor === 'true' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {item.valor === 'true' ? 'Sí' : 'No'}
        </span>
      );
    }

    return <span className="font-mono text-gray-900">{item.valor}</span>;
  };

  const filteredConfig = (config || defaultConfig).filter(c => c.categoria === activeCategoria);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuración del Sistema</h1>
        <p className="mt-1 text-sm text-gray-500">Parámetros y ajustes generales del sistema</p>
      </div>

      <div className="flex gap-6">
        {/* Sidebar categorías */}
        <div className="w-64 shrink-0">
          <nav className="bg-white rounded-lg shadow p-2 space-y-1">
            {categorias.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategoria(cat.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeCategoria === cat.id
                    ? 'bg-primary-50 text-primary-700'
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <span className="text-xl">{cat.icon}</span>
                <span className="font-medium">{cat.label}</span>
              </button>
            ))}
          </nav>

          {/* Info */}
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>⚠️ Precaución:</strong> Modificar estos valores puede afectar el funcionamiento del sistema.
            </p>
          </div>
        </div>

        {/* Contenido */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="font-semibold text-gray-900">
                {categorias.find(c => c.id === activeCategoria)?.label}
              </h2>
            </div>

            {isLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : (
              <div className="divide-y">
                {filteredConfig.map((item) => (
                  <div key={item.clave} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.descripcion}</p>
                        <p className="text-sm text-gray-500 font-mono">{item.clave}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        {renderValue(item, editingKey === item.clave)}
                        
                        {editingKey === item.clave ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveEdit(item.clave)}
                              disabled={saveMutation.isPending}
                              className="px-3 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700"
                            >
                              Guardar
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-3 py-1 border rounded text-sm hover:bg-gray-50"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEdit(item)}
                            className="text-primary-600 hover:text-primary-800 text-sm"
                          >
                            Editar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Acciones adicionales */}
          <div className="mt-6 bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Acciones del Sistema</h3>
            <div className="grid grid-cols-2 gap-4">
              <button className="flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50">
                <span className="text-2xl">📊</span>
                <div className="text-left">
                  <p className="font-medium">Exportar Configuración</p>
                  <p className="text-sm text-gray-500">Descargar como JSON</p>
                </div>
              </button>
              <button className="flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50">
                <span className="text-2xl">📥</span>
                <div className="text-left">
                  <p className="font-medium">Importar Configuración</p>
                  <p className="text-sm text-gray-500">Cargar desde archivo</p>
                </div>
              </button>
              <button className="flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50">
                <span className="text-2xl">🔄</span>
                <div className="text-left">
                  <p className="font-medium">Restaurar Valores</p>
                  <p className="text-sm text-gray-500">Volver a configuración inicial</p>
                </div>
              </button>
              <button className="flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50">
                <span className="text-2xl">🧹</span>
                <div className="text-left">
                  <p className="font-medium">Limpiar Caché</p>
                  <p className="text-sm text-gray-500">Refrescar datos en memoria</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
